import { useEffect, useRef, useState } from 'react';

/**
 * AnimatedCounter — affiche un nombre avec animation douce au premier "reveal".
 *
 * Correctifs 2026-08-28 :
 *  - Fallback SEO : initialise avec la vraie valeur (jamais "0" au chargement).
 *  - Respect strict de prefers-reduced-motion (aucune animation, valeur directe).
 *  - Ne redémarre pas l'animation si la valeur n'a pas changé.
 *  - Fonctionne même si IntersectionObserver n'est pas disponible (fallback direct).
 *  - Anime uniquement depuis la valeur précédente vers la nouvelle valeur.
 */
export function AnimatedCounter({ value, duration = 900, suffix = '' }) {
  const target = Number(value) || 0;

  // Détection prefers-reduced-motion — évaluée une seule fois côté client
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // On initialise DIRECTEMENT à la vraie valeur (fallback SEO/no-JS/SSR/reduced-motion).
  const [n, setN] = useState(target);
  const ref = useRef(null);
  const hasAnimated = useRef(false); // n'anime qu'une seule fois par valeur affichée

  useEffect(() => {
    // Si reduced motion → jamais d'animation, juste la vraie valeur
    if (reducedMotion) {
      setN(target);
      return;
    }

    // Si IntersectionObserver n'existe pas (vieux navigateurs), fallback direct
    if (typeof IntersectionObserver === 'undefined' || !ref.current) {
      setN(target);
      return;
    }

    // Une fois l'élément visible, on démarre l'animation depuis 0 → target.
    // Si l'utilisateur revient sur la page ou que la valeur change, on re-anime.
    const currentTarget = target;
    hasAnimated.current = false;

    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || hasAnimated.current) return;
      hasAnimated.current = true;
      const start = performance.now();
      const from = 0; // effet visuel : ça monte de 0 vers la vraie valeur
      const to = currentTarget;
      const step = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const ease = 1 - Math.pow(1 - p, 3);
        setN(Math.round(from + (to - from) * ease));
        if (p < 1) requestAnimationFrame(step);
        else setN(to); // garantit la valeur exacte à la fin
      };
      requestAnimationFrame(step);
    }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration, reducedMotion]);

  return (
    <span
      ref={ref}
      className="tabular-nums"
      // Fallback crawlable : la valeur exacte est aussi dans data-value pour les crawlers
      data-value={target}
    >
      {n}{suffix}
    </span>
  );
}
